import {
  getEffectiveAmount,
  priceMultiple,
} from '@bbkPricingPage/helpers/amount-calculator';
import { type ActiveItemPrice } from '@bbkPricingPage/helpers/pricing-table-helper';
import {
  applyDifferentialPricing,
  filterPricesByVariant,
  findMatchingDifferentialPrice,
} from '@bbkPricingPage/lib/price-utils';
import { getSubscribedPlanQuantity } from '@bbkPricingPage/lib/subscription-utils';
import {
  AddonType,
  ChargeOnEvent,
  ItemType,
  SubscriptionStatus,
  type Addon,
  type AttachedAddon,
  type Frequency,
  type Item,
  type Price,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';

export type PlanAddonsMapValue = Map<
  string,
  { addonId: string; quantity: number }
>;

export type PlanAddonsMap = {
  [planKey: string]: PlanAddonsMapValue;
};

export type ApplicableAddons = {
  addon: Addon;
  price: Price;
  quantity: number;
};

export const ADDON_COUNT_TOKEN = '{{addon_count}}';

export const DEFAULT_ADDON_SUMMARY_LABEL = ` ${ADDON_COUNT_TOKEN} included`;

/**
 * Resolves the merchant's addon summary template against a live count.
 *
 * Unlike `recurring_price_label`, where an empty value means "hide", an empty,
 * whitespace-only or absent template here means "use the built-in default".
 *
 * The whitespace collapse is required: the editor stores the token with
 * structural spaces around it, so an empty prefix or suffix would otherwise
 * leak a stray space into the rendered label.
 */
export const resolveAddonSummaryLabel = (
  template: string | undefined,
  count: number
): string => {
  const effective = template?.trim() ? template : DEFAULT_ADDON_SUMMARY_LABEL;
  const countText = `${count} ${count === 1 ? 'Addon' : 'Addons'}`;
  return effective
    .split(ADDON_COUNT_TOKEN)
    .join(countText)
    .replace(/\s+/g, ' ')
    .trim();
};

type OptimalPriceOptions = {
  prices: Price[];
  currency: string;
  itemId: string;
  itemType: ItemType;
  frequency?: Frequency;
  variantId?: string;
  parentPeriod?: number;
  parentPeriodUnit?: string;
};

/**
 * Returns the optimal price for an addon or charge.
 * If itemType is ItemType.addon and frequency is provided, selects the price with the closest frequency.
 * If itemType is ItemType.charge, selects the first applicable price.
 * Returns null if no price found.
 */
export const getOptimalPrice = ({
  prices,
  currency,
  itemId,
  itemType,
  frequency,
  variantId,
  parentPeriod,
  parentPeriodUnit,
}: OptimalPriceOptions): Price | null => {
  if (!prices.length) return null;

  // 1. Filter by currency and is_attached
  const compatiblePrices = prices.filter(
    (price) => price.is_attached && price.currency === currency
  );

  // 2. Filter by applicable variantId (or null variant as fallback)
  const filteredPrices = filterPricesByVariant(compatiblePrices, variantId);
  if (!filteredPrices.length) return null;

  let selectedPrice: Price | undefined;

  if (itemType === ItemType.addon && frequency) {
    // 3. For addons: select the price entity with the minimum multiple (> 0)
    const priceOptions = filteredPrices
      .map((price) => ({
        price,
        multiple: priceMultiple(
          frequency.period,
          frequency.period_unit,
          price.period,
          price.period_unit
        ),
      }))
      .filter(({ multiple }) => multiple > 0)
      .sort((a, b) => a.multiple - b.multiple);

    selectedPrice = priceOptions[0]?.price;
  } else {
    // 3. For charges: just pick the first applicable price
    selectedPrice = filteredPrices[0];
  }

  if (!selectedPrice) return null;

  // 4. Check for a differential price only in the selected price entity
  const differentialPrice = findMatchingDifferentialPrice(
    selectedPrice.differential_prices,
    itemId,
    parentPeriod,
    parentPeriodUnit as any
  );
  return differentialPrice
    ? applyDifferentialPricing(selectedPrice, differentialPrice)
    : selectedPrice;
};

export const getMandatoryAddonIds = (item: Item): string[] => {
  return (
    item.attached_addons
      ?.filter((addon) => addon.type === AddonType.mandatory)
      .map((addon) => addon.billing_system_id) || []
  );
};

export const getRecommendedAddonIds = (item: Item): string[] => {
  return (
    item.attached_addons
      ?.filter((addon) => addon.type === AddonType.recommended)
      .map((addon) => addon.billing_system_id) || []
  );
};

export const getAddonDefaultQuantity = (
  item: Item,
  addonId: string
): number => {
  return (
    item.attached_addons?.find((addon) => addon.billing_system_id === addonId)
      ?.quantity || 1
  );
};

export const findAddonById = (addons: Addon[], addonId: string) => {
  return addons.find((addon) => addon.billing_system_id === addonId) || null;
};

export const getSelectedAddonsForPlan = (
  // todo
  itemId: string,
  itemsSelectedAddons: PlanAddonsMap
) => {
  return itemsSelectedAddons[itemId] || new Map();
};

export const getAddonQuantity = (
  addon: Addon,
  selectedItem: Item,
  selectedAddons?: Map<string, { addonId: string; quantity: number }>
) => {
  const selectedAddon = selectedAddons?.get(addon.billing_system_id);
  if (selectedAddon) return selectedAddon.quantity;

  return selectedItem.attached_addons?.find(
    (attachedAddon) =>
      attachedAddon.billing_system_id === addon.billing_system_id
  )?.quantity;
};

export const initializeSelectedAddonsForPlan = (
  plan: Item,
  addons: Addon[],
  currency: string,
  frequency: Frequency,
  variantId?: string,
  subscription?: Subscription,
  isSubscribedPrice?: boolean
): PlanAddonsMapValue => {
  const selectedAddons: PlanAddonsMapValue = new Map();

  const addonIds = [
    ...getMandatoryAddonIds(plan).map((id) => ({
      addonId: id,
      isMandatory: true,
    })),
    ...getRecommendedAddonIds(plan).map((id) => ({
      addonId: id,
      isMandatory: false,
    })),
  ];

  for (const { addonId, isMandatory } of addonIds) {
    // Skip recommended addons if plan is not subscribed
    if (!isSubscribedPrice && !isMandatory) continue;

    const addon = addons.find((a) => a.billing_system_id === addonId);
    if (!addon) continue;

    const addonPrice = getOptimalPrice({
      prices: addon.prices,
      currency,
      itemId: plan.billing_system_id,
      itemType: addon.item_type,
      frequency,
      variantId,
      parentPeriod: frequency.period,
      parentPeriodUnit: frequency.period_unit,
    });
    if (!addonPrice) continue;

    const quantity = getAddonDefaultQuantity(plan, addonId);
    const subscribedQty = getSubscribedPlanQuantity(
      subscription,
      addonPrice.billing_system_id
    );

    // Set addons only if they are mandatory or if its subscribed
    if (isMandatory || subscribedQty) {
      selectedAddons.set(addonId, {
        addonId,
        quantity: subscribedQty ?? quantity,
      });
    }
  }

  return selectedAddons;
};

// const calculateAddonPrice = (
//   addonId: string,
//   addons: Addon[],
//   item: Item,
//   currency: string,
//   frequency: Frequency,
//   variantId?: string
// ): number => {
//   const addon = findAddonById(addons, addonId);
//   if (!addon) {
//     return 0;
//   }

//   const addonPrice = getOptimalPrice({
//     prices: addon.prices,
//     currency,
//     itemId: item.billing_system_id,
//     itemType: addon.item_type,
//     frequency,
//     variantId,
//   });

//   if (!addonPrice) {
//     return 0;
//   }

// };

const isApplicableCharge = (
  attachedCharge: AttachedAddon,
  charge: Addon,
  selectedPlanPrice: ActiveItemPrice,
  subscription?: Subscription,
  isSubscribedPrice?: boolean
): boolean => {
  if (!attachedCharge.charge_on_event || !charge || !selectedPlanPrice)
    return false;

  const subscriptionStatus = subscription?.status as SubscriptionStatus; //todo
  const chargeOnEvent = attachedCharge.charge_on_event;
  const hasTrial =
    selectedPlanPrice.price.trial_period_unit !== null &&
    selectedPlanPrice.price.trial_period_unit !== undefined;
  if (!subscription) {
    return handleForNewUSer(chargeOnEvent, hasTrial);
  } else {
    if (subscriptionStatus === SubscriptionStatus.active) {
      if (
        !isSubscribedPrice &&
        chargeOnEvent === ChargeOnEvent.plan_activation
      ) {
        return true;
      }
      return false;
    } else if (subscriptionStatus === SubscriptionStatus.cancelled) {
      if (
        !isSubscribedPrice &&
        (chargeOnEvent === ChargeOnEvent.plan_activation ||
          chargeOnEvent === ChargeOnEvent.subscription_activation)
      ) {
        return true;
      }
      return false;
    } else if (subscriptionStatus === SubscriptionStatus.in_trial) {
      if (
        !isSubscribedPrice &&
        chargeOnEvent === ChargeOnEvent.plan_activation
      ) {
        return true;
      }
      return false;
    }
  }
  return false;
};

// Move this function to top-level scope
export const handleForNewUSer = (
  chargeOnEvent: ChargeOnEvent,
  hasTrial: boolean
): boolean => {
  switch (chargeOnEvent) {
    case ChargeOnEvent.subscription_creation:
      return true;
    case ChargeOnEvent.subscription_trial_start:
      return hasTrial;
    case ChargeOnEvent.plan_activation:
    case ChargeOnEvent.subscription_activation:
      return true;
    default:
      return false;
  }
};

export const getApplicableAddonsOrCharges = ({
  allAddons,
  selectedPlanPrice,
  priceVariant,
  subscription,
  isSubscribedPrice,
  itemType,
  frequency,
}: {
  allAddons: Addon[];
  selectedPlanPrice: ActiveItemPrice;
  priceVariant?: string;
  subscription?: Subscription;
  isSubscribedPrice?: boolean;
  itemType: ItemType;
  frequency?: Frequency;
}): ApplicableAddons[] => {
  const attachedAddonsOrCharges =
    selectedPlanPrice.item.attached_addons?.filter((addon) =>
      itemType === ItemType.addon
        ? addon.type === AddonType.mandatory
        : addon.item_type === ItemType.charge
    );
  if (!attachedAddonsOrCharges?.length) return [];

  return attachedAddonsOrCharges
    .map((attachedAddonOrCharge) => {
      const addon = allAddons.find(
        (ad) => ad.billing_system_id === attachedAddonOrCharge.billing_system_id
      );

      if (!addon) return null;

      const isApplicableAddonOfTypeCharge =
        itemType === ItemType.charge &&
        isApplicableCharge(
          attachedAddonOrCharge,
          addon,
          selectedPlanPrice,
          subscription,
          isSubscribedPrice
        );

      const price =
        addon.item_type === ItemType.addon
          ? getOptimalPrice({
              prices: addon.prices,
              currency: selectedPlanPrice.price.currency,
              itemId: selectedPlanPrice.item.billing_system_id,
              itemType: addon.item_type,
              variantId: priceVariant,
              frequency: frequency,
              parentPeriod: selectedPlanPrice.price.period,
              parentPeriodUnit: selectedPlanPrice.price.period_unit,
            })
          : isApplicableAddonOfTypeCharge
            ? getOptimalPrice({
                prices: addon.prices,
                currency: selectedPlanPrice.price.currency,
                itemId: selectedPlanPrice.item.billing_system_id,
                itemType: addon.item_type,
                variantId: priceVariant,
                parentPeriod: selectedPlanPrice.price.period,
                parentPeriodUnit: selectedPlanPrice.price.period_unit,
              })
            : null;

      if (!price) return null;

      return { addon: addon, price, quantity: attachedAddonOrCharge.quantity };
    })
    .filter((item): item is ApplicableAddons => item !== null);
};

// TODO: Remove this after validating the new function getApplicableAddonsOrCharges
// export const getApplicableCharges = (
//   allCharges: Addon[],
//   selectedPlanPrice: ActiveItemPrice,
//   priceVariant?: string,
//   subscription?: Subscription,
//   isSubscribedPrice?: boolean
// ): ApplicableAddons[] => {
//   const attachedCharges = selectedPlanPrice.item.attached_addons?.filter(
//     (aa) => aa.item_type === ItemType.charge
//   );
//   if (!attachedCharges?.length) return [];

//   const applicableCharges = attachedCharges
//     .map((attachedCharge) => {
//       const charge = allCharges.find(
//         (ch) => ch.billing_system_id === attachedCharge.billing_system_id
//       );
//       if (
//         charge &&
//         isApplicableCharge(
//           attachedCharge,
//           charge,
//           selectedPlanPrice,
//           subscription,
//           isSubscribedPrice
//         )
//       ) {
//         const price = getOptimalPrice({
//           prices: charge.prices,
//           currency: selectedPlanPrice.price.currency,
//           itemId: selectedPlanPrice.item.billing_system_id,
//           itemType: charge.item_type,
//           variantId: priceVariant,
//         });
//         if (!price) return null;
//         return { addon: charge, price, quantity: attachedCharge.quantity };
//       }
//       return null;
//     })
//     .filter((item): item is ApplicableAddons => item !== null);

//   return applicableCharges;
// };

// TODO: Remove this after validating the new function getApplicableAddonsOrCharges
// export const getApplicableAddons = (
//   allAddons: Addon[],
//   selectedPlanPrice: ActiveItemPrice,
//   priceVariant?: string,
//   frequency?: Frequency
// ): ApplicableAddons[] => {
//   const attachedMandatoryAddons =
//     selectedPlanPrice.item.attached_addons?.filter(
//       (aa) => aa.item_type === ItemType.addon && aa.type === AddonType.mandatory
//     );
//   if (!attachedMandatoryAddons?.length) return [];
//   const activeAddonPricesQuantity = attachedMandatoryAddons
//     .map((attachedAddon) => {
//       const addon = allAddons.find(
//         (a) => a.billing_system_id === attachedAddon.billing_system_id
//       );
//       if (!addon) return null;
//       const addonPrice = getOptimalPrice({
//         prices: addon.prices,
//         currency: selectedPlanPrice.price.currency,
//         itemId: selectedPlanPrice.item.billing_system_id,
//         itemType: addon.item_type,
//         frequency: frequency,
//         variantId: priceVariant,
//       });
//       if (addonPrice) {
//         return {
//           addon,
//           price: addonPrice,
//           quantity: getAddonQuantity(addon, selectedPlanPrice.item),
//         };
//       }
//       return null;
//     })
//     .filter((item): item is ApplicableAddons => item !== null);
//   return activeAddonPricesQuantity;
// };

export const calculateTotalPrice = ({
  applicableAddons,
  frequency,
  itemType,
  skipMonthly = false,
}: {
  applicableAddons: ApplicableAddons[];
  frequency: Frequency;
  itemType: ItemType;
  skipMonthly?: boolean;
}): number => {
  if (!applicableAddons.length) return 0;
  let total = 0;
  applicableAddons.forEach(({ price, quantity }) => {
    const priceData = getEffectiveAmount(
      price,
      quantity,
      frequency,
      undefined,
      skipMonthly
    );
    const multiplier =
      itemType === ItemType.charge
        ? 1
        : priceMultiple(
            frequency.period,
            frequency.period_unit,
            price.period,
            price.period_unit
          );
    total += priceData * multiplier;
  });
  return total;
};

export const calculateTotalPriceWithoutMonthly = ({
  applicableAddons,
  frequency,
  itemType,
}: {
  applicableAddons: ApplicableAddons[];
  frequency: Frequency;
  itemType: ItemType;
}): number => {
  return calculateTotalPrice({
    applicableAddons,
    frequency,
    itemType,
    skipMonthly: true,
  });
};

export const calculateMandatoryAddonsTotalPrice = (
  applicableAddons: ApplicableAddons[],
  frequency: Frequency,
  skipMonthly: boolean = false
): number => {
  return applicableAddons
    .map((addon) => {
      const addonPrice = addon.price;

      const periodMultiplier = priceMultiple(
        frequency.period,
        frequency.period_unit,
        addonPrice.period,
        addonPrice.period_unit
      );

      const quantity = addon.quantity;
      const baseAmount = getEffectiveAmount(
        addonPrice,
        quantity,
        frequency,
        undefined,
        skipMonthly
      );

      return (baseAmount ?? 0) * periodMultiplier;
    })
    .reduce((acc, curr) => acc + curr, 0);
};

export const calculateMandatoryAddonsTotalPriceWithoutMonthly = (
  applicableAddons: ApplicableAddons[],
  frequency: Frequency
): number => {
  return calculateMandatoryAddonsTotalPrice(applicableAddons, frequency, true);
};
